import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { cssMainSelector } from "../lib/cssSelectors";
import MaterialIcon from "./MaterialIcon";
import { withTranslation } from "react-i18next";
import { arrayIsSame } from "../lib/utils";
import DataSpeed from "./DataSpeed";
import MonthlyFee from "./MonthlyFee";
import PriceUpfront from "./PriceUpfront";
import DataCredits from "./DataCredits";
import SmsCredits from "./SmsCredits";
import VoiceCredits from "./VoiceCredits";
import { findPortalDataEntity } from "../lib/portalData";

const Container = styled.div`
  ${p => cssMainSelector(p.theme.tdsId)} {
    * {
      color: ${p => p.theme.nonDefaultFilter.resetFiltersColor};
      font-size: ${p => p.theme.font.sizeSm};
      white-space: normal;
    }
  }
`;

const nonDefaultFilterItemPadding = css `padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.md}`;
const NonDefaultFilterItem = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    background-color: ${p => p.theme.nonDefaultFilter.valueBackgroundColor};
    color: ${p => p.theme.nonDefaultFilter.valueColor};
    display: inline-block;
    font-size: ${p => p.theme.font.sizeSm};
    font-weight: ${p => p.theme.font.weightSemiBold};
    margin-bottom: ${p => p.theme.spacing.sm};
    margin-right: ${p => p.theme.spacing.sm};
    ${nonDefaultFilterItemPadding};
  }
`;

const ResetWrapper = styled.span`
  ${p => cssMainSelector(p.theme.tdsId)} {
    cursor: pointer;
    display: inline-block;
    user-select: none;

    * {
      cursor: pointer;
    }
  }
`;

class NonDefaultFilterLine extends PureComponent {
  render() {
    const { onResetClick, t } = this.props;

    const nonDefaultFilterElements = this.nonDefaultFilterElements();

    if (nonDefaultFilterElements.length > 0) {
      return (
        <Container>
          {nonDefaultFilterElements}
          <ResetWrapper onClick={onResetClick}>
            <MaterialIcon>autorenew</MaterialIcon> {t("resetFilters")}
          </ResetWrapper>
        </Container>
      );
    } else {
      return null;
    }
  }

  nonDefaultFilterElements() {
    const { currentFilters, defaultFilters, portal, t } = this.props;

    const items = [];

    for (const currentFiltersKey of Object.keys(currentFilters)) {
      let filterIsDefault = true;

      if (
        Array.isArray(defaultFilters[currentFiltersKey]) &&
        Array.isArray(currentFilters[currentFiltersKey])
      ) {
        filterIsDefault = arrayIsSame(
          defaultFilters[currentFiltersKey],
          currentFilters[currentFiltersKey]
        );
      } else {
        filterIsDefault =
          defaultFilters[currentFiltersKey] ===
          currentFilters[currentFiltersKey];
      }

      if (!filterIsDefault) {
        if (
          currentFiltersKey === "brandId" &&
          (currentFilters.phoneId.length !== 1 ||
            currentFilters.phoneVariantId.length !== 1)
        ) {
          items.push(
            <NonDefaultFilterItem key="brandId">
              {currentFilters[currentFiltersKey].length === 1 && (
                <span>
                  {t("brandId", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
              {currentFilters[currentFiltersKey].length > 1 && (
                <span>
                  {t("brandIds", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "contractTermInMonths") {
          items.push(
            <NonDefaultFilterItem key="contractTermInMonths">
              {this.contractTermText(currentFilters[currentFiltersKey])}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "currentProviderId") {
          items.push(
            <NonDefaultFilterItem key="currentProviderId">
              {this.currentProviderText(currentFilters[currentFiltersKey])}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "dataSpeedGen") {
          items.push(
            <NonDefaultFilterItem key="dataSpeedGen">
              {this.dataSpeedGenText(currentFilters[currentFiltersKey])}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "maxMonthlyFee") {
          items.push(
            <NonDefaultFilterItem key="maxMonthlyFee">
              {t("max")}{" "}
              <MonthlyFee
                monthlyFee={currentFilters[currentFiltersKey]}
                portal={portal}
                replaceZeros={true}
              />
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "maxPrice") {
          items.push(
            <NonDefaultFilterItem key="maxPrice">
              {t("max")}{" "}
              <PriceUpfront
                amount={currentFilters[currentFiltersKey]}
                portal={portal}
                replaceZeros={true}
              />
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "minDataCredits") {
          items.push(
            <NonDefaultFilterItem key="minDataCredits">
              {t("min")}{" "}
              <DataCredits
                amount={currentFilters[currentFiltersKey]}
                portal={portal}
              />{" "}
              {t("data")}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "minDataSpeedDown") {
          items.push(
            <NonDefaultFilterItem key="minDataSpeedDown">
              {t("min")}{" "}
              <DataSpeed
                amount={currentFilters[currentFiltersKey]}
                portal={portal}
              />
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "minSmsCredits") {
          items.push(
            <NonDefaultFilterItem key="minSmsCredits">
              {t("min")}{" "}
              <SmsCredits amount={currentFilters[currentFiltersKey]} />
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "minVoiceCredits") {
          items.push(
            <NonDefaultFilterItem key="minVoiceCredits">
              {t("min")}{" "}
              <VoiceCredits
                amount={currentFilters[currentFiltersKey]}
                portal={portal}
              />
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "phoneOperatingSystemId") {
          items.push(
            <NonDefaultFilterItem key="phoneOperatingSystemId">
              {currentFilters[currentFiltersKey].length === 1 && (
                <span>
                  {t("phoneOperatingSystemId", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
              {currentFilters[currentFiltersKey].length > 1 && (
                <span>
                  {t("phoneOperatingSystemIds", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "providerId") {
          items.push(
            <NonDefaultFilterItem key="providerId">
              {currentFilters[currentFiltersKey].length === 1 && (
                <span>
                  {t("providerId", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
              {currentFilters[currentFiltersKey].length > 1 && (
                <span>
                  {t("providerIds", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
            </NonDefaultFilterItem>
          );
        } else if (currentFiltersKey === "shopId") {
          items.push(
            <NonDefaultFilterItem key="shopId">
              {currentFilters[currentFiltersKey].length === 1 && (
                <span>
                  {t("shopId", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
              {currentFilters[currentFiltersKey].length > 1 && (
                <span>
                  {t("shopIds", {
                    count: currentFilters[currentFiltersKey].length
                  })}
                </span>
              )}
            </NonDefaultFilterItem>
          );
        }
      }
    }

    return items;
  }

  contractTermText(contractTermInMonths) {
    const { t } = this.props;
    const texts = {
      "0": t("contractTermNoPreference"),
      "1": t("contractTermOneMonth"),
      "12": t("contractTermOneYear"),
      "24": t("contractTermTwoYears")
    };

    if (contractTermInMonths.length < 1) {
      return `${t("contractTermPrefix")} ${t("contractTermNoPreference")}`;
    } else {
      return `${t("contractTermPrefix")} ${
        texts[contractTermInMonths[contractTermInMonths.length - 1].toString()]
      }`;
    }
  }

  currentProviderText() {
    const { currentFilters, portalData, t } = this.props;
    const currentProvider = findPortalDataEntity(
      portalData,
      "provider",
      currentFilters.currentProviderId
    );

    if (currentProvider != null) {
      return `${t("currentProvider")} ${currentProvider.name}`;
    } else {
      return null;
    }
  }

  dataSpeedGenText(dataSpeedGen) {
    const { offerType, portalData, t } = this.props;

    if (dataSpeedGen.length < 1) {
      const dataSpeedGens = portalData.entities.dataSpeedGens[`${offerType}Ids`];
      const sortedDataSpeedGens = [...dataSpeedGens].sort();
      if (sortedDataSpeedGens.length > 0) {
        return <span>{t("dataSpeedGenText", { gen: sortedDataSpeedGens[0] })}</span>;
      } else {
        return null;
      }
    } else {
      return <span>{t("dataSpeedGenText", { gen: dataSpeedGen[0] })}</span>;
    }
  }
}

NonDefaultFilterLine.propTypes = {
  currentFilters: PropTypes.object.isRequired,
  defaultFilters: PropTypes.object.isRequired,
  offerType: PropTypes.string.isRequired,
  onResetClick: PropTypes.func.isRequired,
  portal: PropTypes.object.isRequired,
  portalData: PropTypes.object.isRequired
};

export default withTranslation(["nonDefaultFilters"])(NonDefaultFilterLine);
